import { withGitpodSize } from "@/assets/icons/utils";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { Tabs, TabsList, TabsTrigger } from "@/components/podkit/tabs/Tabs";
import { Tooltip } from "@/components/Tooltip";
import IconBook from "central-icons/IconBook";
import IconBranch from "central-icons/IconBranch";
import IconCodeBrackets from "central-icons/IconCodeBrackets";
import { type FC } from "react";

export type EnvironmentViewMode = "details" | "vscode" | "changes";

export type ViewModeToggleProps = PropsWithClassName & {
    canViewVSCodeBrowser: boolean;
    /** When true, the Code tab is hidden entirely (e.g., when VS Code browser is disallowed by org policy) */
    hideCodeTab?: boolean;
    /** When true, the Changes tab is hidden entirely (e.g., when review feature is enabled) */
    hideChangesTab?: boolean;
    value: EnvironmentViewMode;
    onValueChange: (value: EnvironmentViewMode) => void;
    "data-testid"?: string;
};

export const ViewModeToggle: FC<ViewModeToggleProps> = ({
    canViewVSCodeBrowser,
    hideCodeTab = false,
    hideChangesTab = false,
    value,
    onValueChange,
    className,
    "data-testid": dataTestId,
}) => {
    return (
        <Tabs
            value={value}
            onValueChange={(newValue: string) => {
                onValueChange(newValue as EnvironmentViewMode);
            }}
            defaultValue="changes"
            className={cn("w-full", className)}
            data-testid={dataTestId}
        >
            <TabsList className="w-fit flex-row">
                {!hideChangesTab && (
                    <TabsTrigger
                        value="changes"
                        aria-label="Changes view"
                        LeadingIcon={withGitpodSize(IconBranch)}
                        className="size-7 gap-0 @xs:size-auto @xs:gap-2"
                    >
                        <span className="sr-only truncate @xs:not-sr-only">Changes</span>
                    </TabsTrigger>
                )}

                <TabsTrigger
                    value="details"
                    aria-label="Details view"
                    LeadingIcon={withGitpodSize(IconBook)}
                    className="size-7 gap-0 @xs:size-auto @xs:gap-2"
                >
                    <span className="sr-only truncate @xs:not-sr-only">Environment</span>
                </TabsTrigger>

                {!hideCodeTab && (
                    <Tooltip
                        content={canViewVSCodeBrowser ? "VS Code Browser view" : "Start environment to view code"}
                        usePortal
                    >
                        {/* We need this div wrapper as the Tooltip otherwise overwrites the data-state attribute of the TabsTrigger */}
                        <div className="contents">
                            <TabsTrigger
                                disabled={!canViewVSCodeBrowser}
                                value="vscode"
                                aria-label="VS Code Browser view"
                                LeadingIcon={withGitpodSize(IconCodeBrackets)}
                                className="size-7 gap-0 @xs:size-auto @xs:gap-2"
                            >
                                <span className="sr-only truncate @xs:not-sr-only">Code</span>
                            </TabsTrigger>
                        </div>
                    </Tooltip>
                )}
            </TabsList>
        </Tabs>
    );
};
